import React, { FC, useState, useEffect } from 'react'
import { useAlert } from 'react-alert'

import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import {
  updateWorkCategories,
} from '../../../states/containers/setting/profileSlice'

import { useTranslation } from 'react-i18next'
import { secToHourMinute } from '../../../utils/date'
import { Occupation, WorkStyle } from '../../../states/types/member'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface WorkCategoryDetail {
  occupation_id: number | null,
  work_style_ids: number[],
  base_work_time: number,
  base_active_time: number,
  base_inactive_time: number,
}

interface Props {
  closeCallback: () => void
  type: string
}

const WorkStyleDetail: FC<Props> = (props) => {
  const {
    closeCallback,
    type,
  } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    profile,
    login,
  } = useSelector((state: RootState) => state)

  const initialInput = {
    occupation_id: null,
    work_style_ids: [],
    base_work_time: 0,
    base_active_time: 0,
    base_inactive_time: 0,
  }

  const [workCategoryData, setWorkCategoryData] = useState<WorkCategoryDetail>(initialInput)

  const [isSubmit, setIsSubmit] = useState(true)

  const [isOver24, setIsOver24] = useState(false)

  const [error, setError] = useState<{type: string, text: string}[]>([])

  const closeModal = () => {
    closeCallback()
  }

  const handleSubmit = () => {
    setIsSubmit(false)
    dispatch(
      updateWorkCategories(
        workCategoryData,
        () => {
          setIsSubmit(true)
          alert.success(t('form.alert.profile_updated'))
          closeModal()
        },
        () => {
          setIsSubmit(true)
        },
      ),
    )
  }

  const changeActiveTime = (value: number) => {
    if (value + workCategoryData.base_inactive_time > 86400) {
      setIsOver24(true)
    } else {
      setIsOver24(false)
    }
    setWorkCategoryData({
      ...workCategoryData,
      base_work_time: value + workCategoryData.base_inactive_time,
      base_active_time: value,
    })
  }

  const changeInactiveTime = (value: number) => {
    if (value + workCategoryData.base_active_time > 86400) {
      setIsOver24(true)
    } else {
      setIsOver24(false)
    }
    setWorkCategoryData({
      ...workCategoryData,
      base_work_time: value + workCategoryData.base_active_time,
      base_inactive_time: value,
    })
  }

  useEffect(() => {
    if (!profile || profile.isLoading) return
    setWorkCategoryData({
      ...initialInput,
      occupation_id: profile.occupation_id,
      work_style_ids: profile.work_style_ids,
      base_work_time: profile.base_work_time,
      base_active_time: profile.base_active_time,
      base_inactive_time: profile.base_inactive_time,
    })
  }, [profile])

  useEffect(() => {
    const tmpError: {type: string, text: string}[] = []
    if (!workCategoryData.occupation_id) {
      tmpError.push({ type: "occupation_id", text: "職種が設定されていません" })
    }
    if (!workCategoryData.work_style_ids.length || workCategoryData.work_style_ids[0] === 0) {
      tmpError.push({ type: "work_style_ids", text: "働き方の特徴が設定されていません" })
    }
    if (
      !workCategoryData.base_active_time
      || !workCategoryData.base_work_time
      || tmpError.length
    ) {
      setError(tmpError)
      setIsSubmit(false)
    } else {
      setError([])
      setIsSubmit(true)
    }
  }, [workCategoryData])

  return (
    <>
      {
        login.function_enabled_state?.ai_work_assessment ? (
          <>
            {
              type === "force" ? (
                <div className="work_style_header force">
                  <div className="title">
                    未設定の項目があります。
                  </div>
                  <div className="description">
                    プロフィールに
                    <strong>あなたの働き方</strong>
                    を設定しましょう。よりそれぞれのユーザーにあったレポート結果になります。
                  </div>
                </div>
              ) : (
                <div className="work_style_header">
                  <div className="title">
                    あなたの働き方をレポートに反映させることができます。
                  </div>
                  <div className="description">
                    それぞれの働き方の特徴や職種によって評価における各指標の適性範囲が異なります。あなたの働き方を設定することで、よりあなたの働き方にあったレポート結果を得ることができます。
                  </div>
                </div>
              )
            }
          </>
        ) : (
          <>
            <div className="work_style_header">
              <div className="title">
                あなたの働き方を教えてください
              </div>
            </div>
          </>
        )
      }
      <div className="work_style_body">
        <div
          className="left_area"
          style={{
            width: login.function_enabled_state?.ai_work_assessment ? "30%" : "70%",
          }}
        >
          {
            login.function_enabled_state?.ai_work_assessment && (
              <div className="title">
                あなたの働き方について
              </div>
            )
          }
          <div className="detail_area">
            <div className="title">
              職種
            </div>
            <div className="field change_icon">
              <div
                style={{
                  width: "70%",
                  position: "relative",
                }}
              >
                <select
                  style={{
                    cursor: "pointer",
                  }}
                  value={workCategoryData.occupation_id || 0}
                  onChange={(e) => {
                    setWorkCategoryData({
                      ...workCategoryData,
                      occupation_id: Number(e.target.value),
                    })
                  }}
                >
                  {
                    (type === "tutorial" || type === "force") && (
                      <option value="0">
                        未設定
                      </option>
                    )
                  }
                  {
                    profile.occupationList.map((val: Occupation) => (
                      <option key={val.id} value={val.id}>
                        {t(val.name)}
                      </option>
                    ))
                  }
                </select>
                <FontAwesomeIcon
                  icon={['fas', 'angle-down']}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    fontSize: "16px",
                    pointerEvents: "none",
                  }}
                />
              </div>
              <div className="arrow">
                →
              </div>
              {
                workCategoryData.occupation_id ? (
                  <img
                    alt=""
                    src={`/static/images/occupation/${workCategoryData.occupation_id}.png`}
                    className="icon"
                  />
                ) : (
                  <img
                    alt=""
                    src="/static/images/occupation/0.png"
                    className="icon"
                  />
                )
              }
            </div>
            {
              !!error.find((v) => v.type === "occupation_id") && (
                <div className="error">
                  {error.find((v) => v.type === "occupation_id")?.text}
                </div>
              )
            }
          </div>
          <div className="detail_area">
            <div className="title">
              働き方の特徴
            </div>
            <div className="field change_icon">
              <div
                style={{
                  width: "70%",
                  position: "relative",
                }}
              >
                <select
                  style={{
                    cursor: "pointer",
                  }}
                  value={Number(workCategoryData.work_style_ids)}
                  onChange={(e) => {
                    setWorkCategoryData({
                      ...workCategoryData,
                      work_style_ids: [Number(e.target.value)],
                    })
                  }}
                >
                  {
                    (type === "tutorial" || type === "force") && (
                      <option value="0">
                        未設定
                      </option>
                    )
                  }
                  {
                    profile.work_style_list.map((val: WorkStyle) => (
                      <option key={val.name} value={val.id}>
                        {val.description}
                      </option>
                    ))
                  }
                </select>
                <FontAwesomeIcon
                  icon={['fas', 'angle-down']}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    fontSize: "16px",
                    pointerEvents: "none",
                  }}
                />
              </div>
              <div className="arrow">
                →
              </div>
              {
                workCategoryData.work_style_ids?.length && workCategoryData.work_style_ids[0] !== 0 ? (
                  <img
                    alt=""
                    src={`/static/images/work_style/${workCategoryData.work_style_ids[0]}.png`}
                    className="icon"
                  />
                ) : (
                  <img
                    alt=""
                    src="/static/images/work_style/0.png"
                    className="icon"
                  />
                )
              }
            </div>
            {
              !!error.find((v) => v.type === "work_style_ids") && (
                <div className="error">
                  {error.find((v) => v.type === "work_style_ids")?.text}
                </div>
              )
            }
          </div>
          <div className="detail_area">
            <div className="title">
              標準労働時間
            </div>
            <div className="field">
              <div
                style={{
                  width: "100%",
                  position: "relative",
                }}
              >
                <select
                  style={{
                    cursor: "pointer",
                  }}
                  value={Number(workCategoryData.base_active_time)}
                  onChange={(e) => changeActiveTime(Number(e.target.value))}
                >
                  {
                    Array(288).fill(null).map((v, i) => (
                      <option key={`base_active_time_${i * 300}`} value={i * 300}>
                        {secToHourMinute(i * 300)}
                      </option>
                    ))
                  }
                </select>
                <FontAwesomeIcon
                  icon={['fas', 'angle-down']}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    fontSize: "16px",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>
          </div>
          <div className="detail_area">
            <div className="title">
              標準休憩時間
            </div>
            <div className="field">
              <div
                style={{
                  width: "100%",
                  position: "relative",
                }}
              >
                <select
                  style={{
                    cursor: "pointer",
                  }}
                  value={Number(workCategoryData.base_inactive_time)}
                  onChange={(e) => changeInactiveTime(Number(e.target.value))}
                >
                  {
                    Array(288).fill(null).map((v, i) => (
                      <option key={`base_inactive_time_${i * 300}`} value={i * 300}>
                        {secToHourMinute(i * 300)}
                      </option>
                    ))
                  }
                </select>
                <FontAwesomeIcon
                  icon={['fas', 'angle-down']}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    fontSize: "16px",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className="detail_area"
            style={{
              marginBottom: "24px",
            }}
          >
            <div className="title">
              標準勤務時間
            </div>
            <div className="field">
              <div className="dummy_input">
                <div>
                  {secToHourMinute(workCategoryData.base_work_time)}
                </div>
                {
                  workCategoryData.base_inactive_time ? (
                    <div>
                      {`(休憩時間${secToHourMinute(workCategoryData.base_inactive_time)}を含む)`}
                    </div>
                  ) : (
                    <></>
                  )
                }
              </div>
            </div>
            {
              isOver24 ? (
                <div className="error">
                  ※合計時間が24時間を超えています
                </div>
              ) : (
                <></>
              )
            }
          </div>
          <button
            className="submit_button"
            type="button"
            onClick={() => handleSubmit()}
            disabled={!isSubmit || isOver24}
          >
            更新
          </button>
        </div>
        <div
          className="right_area"
          style={{
            display: login.function_enabled_state?.ai_work_assessment ? "block" : "none",
          }}
        >
          <div className="title_label">
            レポート画面
          </div>
          <div className="big_arrow">
            <div />
            <div />
          </div>
          <div className="image_example">
            <img
              alt=""
              src="/static/images/profile/work_setting_ex.png"
            />
          </div>
          <div className="legend_area">
            <div className="legend_left_area">
              <div className="title_area">
                <hr />
                <div className="title">
                  働き方
                </div>
              </div>
              <div>
                {
                  profile.work_style_list.map((work_style) => (
                    <div
                      className="legend"
                      key={`work_style_${work_style.id}`}
                    >
                      <img
                        alt=""
                        src={`/static/images/work_style/${work_style.id}.png`}
                        className="icon"
                      />
                      {work_style.description}
                    </div>
                  ))
                }
              </div>
            </div>
            <div className="legend_right_area">
              <div className="title_area">
                <hr />
                <div className="title">
                  職種
                </div>
              </div>
              <div className="legend_wrap">
                {
                  profile.occupationList.map((val: Occupation) => (
                    <div
                      className="legend"
                      key={`occupation_${val.id}`}
                    >
                      <img
                        alt=""
                        src={`/static/images/occupation/${val.id}.png`}
                        className="icon"
                      />
                      {t(val.name)}
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default WorkStyleDetail
